import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Popover,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import dico from "../../../util/src/language/dico";
import ColorPicker from "./ColorPicker";
import {
  EDITOR_PICKER_POPOVER_Z_INDEX,
  editorPickerSelectMenuProps,
} from "./editorPickerPopoverZIndex";

export type GradientPickerProps = {
  value: string;
  disabled?: boolean;
  onChange: (nextValue: string) => void;
};

type GradientType =
  | "linear-gradient"
  | "repeating-linear-gradient"
  | "radial-gradient"
  | "repeating-radial-gradient"
  | "conic-gradient"
  | "repeating-conic-gradient";

type LinearMode = "angle" | "side";

type ColorStop = {
  color: string;
  positionA: string;
  positionB: string;
  hint: string;
};

type GradientState = {
  type: GradientType;
  colorSpace: string;
  hueMethod: string;
  linearMode: LinearMode;
  linearAngle: string;
  linearSide: string;
  radialShape: string;
  radialSize: string;
  radialPosition: string;
  conicFrom: string;
  conicPosition: string;
  stops: ColorStop[];
  isEmpty: boolean;
  rawMode: boolean;
  raw: string;
};

const GRADIENT_TYPES: GradientType[] = [
  "linear-gradient",
  "repeating-linear-gradient",
  "radial-gradient",
  "repeating-radial-gradient",
  "conic-gradient",
  "repeating-conic-gradient",
];

const COLOR_SPACES = [
  "",
  "srgb",
  "srgb-linear",
  "display-p3",
  "a98-rgb",
  "prophoto-rgb",
  "rec2020",
  "lab",
  "oklab",
  "hsl",
  "hwb",
  "lch",
  "oklch",
  "xyz",
  "xyz-d50",
  "xyz-d65",
];

const HUE_METHODS = ["", "shorter", "longer", "increasing", "decreasing"];
const POLAR_SPACES = new Set(["hsl", "hwb", "lch", "oklch"]);
const LINEAR_SIDE_OPTIONS = [
  "to top",
  "to right",
  "to bottom",
  "to left",
  "to top right",
  "to top left",
  "to bottom right",
  "to bottom left",
];
const RADIAL_SIZE_OPTIONS = [
  "closest-side",
  "closest-corner",
  "farthest-side",
  "farthest-corner",
];
const POSITION_OPTIONS = [
  "center",
  "top",
  "bottom",
  "left",
  "right",
  "top left",
  "top right",
  "bottom left",
  "bottom right",
];
const LINEAR_SIDE_LABEL_KEYS: Record<string, string> = {
  "to top": "linearSideToTop",
  "to right": "linearSideToRight",
  "to bottom": "linearSideToBottom",
  "to left": "linearSideToLeft",
  "to top right": "linearSideToTopRight",
  "to top left": "linearSideToTopLeft",
  "to bottom right": "linearSideToBottomRight",
  "to bottom left": "linearSideToBottomLeft",
};
const RADIAL_SIZE_LABEL_KEYS: Record<string, string> = {
  "closest-side": "radialSizeClosestSide",
  "closest-corner": "radialSizeClosestCorner",
  "farthest-side": "radialSizeFarthestSide",
  "farthest-corner": "radialSizeFarthestCorner",
};
const POSITION_LABEL_KEYS: Record<string, string> = {
  center: "positionCenter",
  top: "positionTop",
  bottom: "positionBottom",
  left: "positionLeft",
  right: "positionRight",
  "top left": "positionTopLeft",
  "top right": "positionTopRight",
  "bottom left": "positionBottomLeft",
  "bottom right": "positionBottomRight",
};

const NEW_STOP: ColorStop = {
  color: "#ffffff",
  positionA: "",
  positionB: "",
  hint: "",
};

const DEFAULT_STATE: GradientState = {
  type: "linear-gradient",
  colorSpace: "",
  hueMethod: "",
  linearMode: "angle",
  linearAngle: "90deg",
  linearSide: "to right",
  radialShape: "ellipse",
  radialSize: "farthest-corner",
  radialPosition: "center",
  conicFrom: "0deg",
  conicPosition: "center",
  stops: [],
  isEmpty: true,
  rawMode: false,
  raw: "",
};

const clean = (v: string | undefined): string => (v ?? "").trim();
const NUMBER_ONLY_REGEX = /^-?\d+(?:[.,]\d+)?$/;

const normalizeStopInputValue = (
  rawValue: string,
  isConic: boolean,
): string => {
  const trimmed = clean(rawValue);
  if (!trimmed) return "";
  if (isConic) return trimmed;
  if (trimmed.endsWith("%")) {
    return trimmed.slice(0, -1).trim();
  }
  return trimmed;
};

const normalizeAngleInputValue = (rawValue: string): string => {
  const trimmed = clean(rawValue);
  if (!trimmed) return "";
  if (trimmed.endsWith("deg")) {
    return trimmed.slice(0, -3).trim();
  }
  return trimmed;
};

const parseAngleInputValue = (rawValue: string): string => {
  const trimmed = clean(rawValue).replace(",", ".");
  if (!trimmed) return "";
  if (NUMBER_ONLY_REGEX.test(trimmed)) {
    return `${trimmed}deg`;
  }
  return trimmed;
};

const withCurrentOption = (options: string[], current: string): string[] => {
  const trimmedCurrent = clean(current);
  if (!trimmedCurrent || options.includes(trimmedCurrent)) return options;
  return [trimmedCurrent, ...options];
};

const getTypeLabel = (
  type: GradientType,
  labels: Record<string, string>,
): string => {
  switch (type) {
    case "linear-gradient":
      return labels.typeLinear ?? "linear";
    case "repeating-linear-gradient":
      return labels.typeRepeatingLinear ?? "repeat linear";
    case "radial-gradient":
      return labels.typeRadial ?? "radial";
    case "repeating-radial-gradient":
      return labels.typeRepeatingRadial ?? "repeat radial";
    case "conic-gradient":
      return labels.typeConic ?? "conic";
    case "repeating-conic-gradient":
      return labels.typeRepeatingConic ?? "repeat conic";
    default:
      return type;
  }
};

const getOptionLabel = (
  option: string,
  keyMap: Record<string, string>,
  labels: Record<string, string>,
): string => {
  const key = keyMap[option];
  if (!key) return option;
  return labels[key] ?? option;
};

const parseStopInputValue = (rawValue: string, isConic: boolean): string => {
  const trimmed = clean(rawValue).replace(",", ".");
  if (!trimmed) return "";
  if (isConic) return trimmed;
  if (NUMBER_ONLY_REGEX.test(trimmed)) {
    return `${trimmed}%`;
  }
  return trimmed;
};

const buildInterpolation = (state: GradientState): string => {
  if (!state.colorSpace) return "";
  const hue =
    POLAR_SPACES.has(state.colorSpace) && state.hueMethod
      ? ` ${state.hueMethod} hue`
      : "";
  return `in ${state.colorSpace}${hue}`;
};

const buildColorStop = (stop: ColorStop): string => {
  const parts = [
    clean(stop.color) || "transparent",
    clean(stop.positionA),
    clean(stop.positionB),
  ].filter(Boolean);
  return parts.join(" ");
};

const buildStopList = (stops: ColorStop[]): string => {
  const entries: string[] = [];
  stops.forEach((stop, i) => {
    entries.push(buildColorStop(stop));
    if (i < stops.length - 1 && clean(stop.hint)) {
      entries.push(clean(stop.hint));
    }
  });
  return entries.join(", ");
};

const buildGradient = (state: GradientState): string => {
  const headParts: string[] = [];
  const interpolation = buildInterpolation(state);

  if (state.type.includes("linear")) {
    headParts.push(
      state.linearMode === "angle"
        ? clean(state.linearAngle)
        : clean(state.linearSide),
    );
  }

  if (state.type.includes("radial")) {
    const geometry = [
      clean(state.radialShape),
      clean(state.radialSize),
      clean(state.radialPosition) ? `at ${clean(state.radialPosition)}` : "",
    ]
      .filter(Boolean)
      .join(" ");
    headParts.push(geometry);
  }

  if (state.type.includes("conic")) {
    const from = clean(state.conicFrom) ? `from ${clean(state.conicFrom)}` : "";
    const at = clean(state.conicPosition)
      ? `at ${clean(state.conicPosition)}`
      : "";
    headParts.push([from, at].filter(Boolean).join(" "));
  }

  if (interpolation) headParts.push(interpolation);

  const stops = buildStopList(state.stops);
  const prefix = headParts.filter(Boolean).join(" ");
  const body = prefix ? `${prefix}, ${stops}` : stops;
  return `${state.type}(${body})`;
};

const resolveGradient = (state: GradientState): string => {
  if (state.rawMode) return state.raw.trim();
  if (state.stops.length === 0) return "";
  if (state.isEmpty) return "";
  return buildGradient(state);
};

const parseGradientToState = (value: string): GradientState => {
  const trimmed = value.trim();
  if (!trimmed || trimmed === "none") return { ...DEFAULT_STATE };

  const typeMatch = trimmed.match(
    /^(repeating-)?(linear|radial|conic)-gradient\(/,
  );
  if (!typeMatch) {
    return {
      ...DEFAULT_STATE,
      isEmpty: false,
      rawMode: true,
      raw: trimmed,
    };
  }

  const type =
    (typeMatch[0].slice(0, -1) as GradientType) || DEFAULT_STATE.type;
  const state: GradientState = {
    ...DEFAULT_STATE,
    type,
    isEmpty: false,
    stops: [],
  };

  const inner = trimmed.slice(
    trimmed.indexOf("(") + 1,
    trimmed.lastIndexOf(")"),
  );

  const tokens: string[] = [];
  let depth = 0;
  let current = "";
  for (const char of inner) {
    if (char === "(") depth++;
    else if (char === ")") depth--;
    else if (char === "," && depth === 0) {
      tokens.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }
  if (current.trim()) tokens.push(current.trim());

  if (tokens.length === 0) return state;

  let stopStartIndex = 0;

  if (type.includes("linear")) {
    const first = tokens[0] ?? "";
    if (first.startsWith("to ")) {
      state.linearMode = "side";
      state.linearSide = first.replace(/\s*in\s+.*$/, "").trim();
      stopStartIndex = 1;
    } else if (/^-?[0-9]/.test(first) || /deg|turn|rad|grad/.test(first)) {
      state.linearMode = "angle";
      state.linearAngle = first.replace(/\s*in\s+.*$/, "").trim();
      stopStartIndex = 1;
    }
  }

  if (type.includes("radial")) {
    const first = tokens[0] ?? "";
    if (
      /circle|ellipse|closest|farthest|at\s/.test(first) ||
      /^\d+px/.test(first)
    ) {
      const atIndex = first.indexOf(" at ");
      if (atIndex !== -1) {
        const shapeSize = first.slice(0, atIndex).trim();
        state.radialPosition = first.slice(atIndex + 4).trim();
        const shapeParts = shapeSize.split(/\s+/);
        if (shapeParts[0] === "circle" || shapeParts[0] === "ellipse") {
          state.radialShape = shapeParts[0];
          state.radialSize = shapeParts.slice(1).join(" ") || "farthest-corner";
        } else {
          state.radialSize = shapeSize || "farthest-corner";
        }
      } else {
        const shapeParts = first.split(/\s+/);
        if (shapeParts[0] === "circle" || shapeParts[0] === "ellipse") {
          state.radialShape = shapeParts[0];
          state.radialSize = shapeParts.slice(1).join(" ") || "farthest-corner";
        } else {
          state.radialSize = first || "farthest-corner";
        }
      }
      stopStartIndex = 1;
    }
  }

  if (type.includes("conic")) {
    const first = tokens[0] ?? "";
    if (first.startsWith("from ") || first.startsWith("at ")) {
      const fromMatch = first.match(/from\s+([^\s]+)/);
      const atMatch = first.match(/at\s+(.+)/);
      if (fromMatch?.[1]) state.conicFrom = fromMatch[1];
      if (atMatch?.[1]) state.conicPosition = atMatch[1];
      stopStartIndex = 1;
    }
  }

  const firstFull = tokens[stopStartIndex - 1] ?? tokens[0] ?? "";
  const inMatch = firstFull.match(
    /\bin\s+([\w-]+)(?:\s+(shorter|longer|increasing|decreasing)\s+hue)?/,
  );
  if (inMatch?.[1]) {
    state.colorSpace = inMatch[1];
    if (inMatch[2]) state.hueMethod = inMatch[2];
  }

  const stopTokens = tokens.slice(stopStartIndex);
  if (stopTokens.length >= 2) {
    state.stops = stopTokens.map((token) => {
      const parts = token.trim().split(/\s+/);
      const color = parts[0] ?? "transparent";
      return {
        color,
        positionA: parts[1] ?? "",
        positionB: parts[2] ?? "",
        hint: "",
      };
    });
  }

  return state;
};

const checkerboardBg =
  "linear-gradient(45deg, #e2e8f0 25%, transparent 25%), " +
  "linear-gradient(-45deg, #e2e8f0 25%, transparent 25%), " +
  "linear-gradient(45deg, transparent 75%, #e2e8f0 75%), " +
  "linear-gradient(-45deg, transparent 75%, #e2e8f0 75%)";

/**
 * Edits a CSS gradient value with full support for linear, radial
 * and conic gradients (including repeating variants), color space
 * interpolation, multi-stop editing via ColorPicker, and a raw CSS
 * fallback mode for non-modelled syntaxes.
 *
 * @param value - Current CSS gradient string.
 * @param disabled - Disables interaction.
 * @param onChange - Callback with the updated CSS string.
 */
const GradientPicker: React.FC<GradientPickerProps> = ({
  value,
  disabled = false,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const popupOpen = Boolean(anchorEl);

  const labels =
    (
      dico().licensedPortal?.layoutEditor as {
        gradientPicker?: Record<string, string>;
      }
    )?.gradientPicker ?? ({} as Record<string, string>);

  const [draft, setDraft] = useState<GradientState>(() =>
    parseGradientToState(value),
  );

  useEffect(() => {
    if (popupOpen) return;
    setDraft(parseGradientToState(value));
  }, [value, popupOpen]);

  const state = draft;

  const emit = useCallback(
    (partial: Partial<GradientState>) => {
      const next = { ...state, ...partial, isEmpty: false };
      if (partial.stops) next.stops = partial.stops;
      setDraft(next);
      onChange(resolveGradient(next));
    },
    [state, onChange],
  );

  const updateStop = useCallback(
    (index: number, patch: Partial<ColorStop>) => {
      const nextStops = state.stops.map((s, i) =>
        i === index ? { ...s, ...patch } : s,
      );
      emit({ stops: nextStops });
    },
    [state.stops, emit],
  );

  const addStop = useCallback(() => {
    const nextStops = [...state.stops, { ...NEW_STOP }];
    emit({ stops: nextStops });
  }, [state.stops, emit]);

  const removeStop = useCallback(
    (index: number) => {
      emit({ stops: state.stops.filter((_, i) => i !== index) });
    },
    [state.stops, emit],
  );

  const gradientCss = useMemo(() => resolveGradient(state), [state]);

  const isLinear = state.type.includes("linear");
  const isRadial = state.type.includes("radial");
  const isConic = state.type.includes("conic");
  const isPolarSpace = POLAR_SPACES.has(state.colorSpace);

  return (
    <>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={(e) => {
          if (!disabled) setAnchorEl(e.currentTarget as HTMLElement);
        }}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setAnchorEl(e.currentTarget as HTMLElement);
          }
        }}
        sx={{
          border: "1px solid #d8e0eb",
          borderRadius: "8px",
          backgroundColor: "#fbfcfe",
          p: "3px 8px",
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.55 : 1,
          "&:hover": {
            borderColor: disabled ? "#d8e0eb" : "#94a3b8",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: 30,
            borderRadius: "6px",
            overflow: "hidden",
            border: "1px solid #c7d2e0",
            background: checkerboardBg,
            backgroundSize: "12px 12px",
            backgroundPosition: "0 0, 0 6px, 6px -6px, -6px 0",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: gradientCss,
            }}
          />
        </Box>
      </Box>

      <Popover
        open={popupOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          root: {
            sx: { zIndex: EDITOR_PICKER_POPOVER_Z_INDEX },
          },
        }}
      >
        <Box
          sx={{
            width: 420,
            maxHeight: "80vh",
            overflow: "auto",
            background: "#fff",
            borderRadius: "12px",
            p: 1.5,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Typography
              sx={{ fontSize: 13, fontWeight: 600, color: "#1f2937" }}
            >
              {labels.title ?? "Gradient"}
            </Typography>
            <Tooltip title={labels.reset ?? ""}>
              <span>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() => {
                    setDraft({ ...DEFAULT_STATE, stops: [] });
                    onChange("");
                  }}
                >
                  <RestartAltIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Type chips */}
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={sectionLabelSx}>
              {labels.typeLabel ?? "Type"}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {GRADIENT_TYPES.map((type) => (
                <Box
                  key={type}
                  onClick={() => !disabled && emit({ type })}
                  sx={{
                    px: 1,
                    py: 0.4,
                    borderRadius: "6px",
                    border: "1px solid",
                    borderColor: state.type === type ? "#4f9cff" : "#c7d2e0",
                    color: state.type === type ? "#0f3f79" : "#334155",
                    fontWeight: state.type === type ? 600 : 400,
                    bgcolor:
                      state.type === type
                        ? "rgba(79,156,255,0.18)"
                        : "transparent",
                    fontSize: 12,
                    cursor: disabled ? "not-allowed" : "pointer",
                    userSelect: "none",
                    "&:hover": {
                      borderColor: disabled ? undefined : "#94a3b8",
                      bgcolor: state.type === type ? undefined : "#f1f5f9",
                    },
                  }}
                >
                  {getTypeLabel(type, labels)}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Linear controls */}
          {isLinear && (
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={sectionLabelSx}>
                {labels.linearSection ?? "Linear"}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1,
                }}
              >
                <Box>
                  <Typography sx={sectionLabelSx}>
                    {labels.direction ?? "Direction"}
                  </Typography>
                  <Select
                    size="small"
                    fullWidth
                    value={state.linearMode}
                    disabled={disabled}
                    onChange={(e) =>
                      emit({ linearMode: e.target.value as LinearMode })
                    }
                    sx={selectSx}
                    MenuProps={editorPickerSelectMenuProps}
                  >
                    <MenuItem value="angle">
                      {labels.angleMode ?? "Angle"}
                    </MenuItem>
                    <MenuItem value="side">
                      {labels.sideMode ?? "Side / corner"}
                    </MenuItem>
                  </Select>
                </Box>
                {state.linearMode === "angle" ? (
                  <Box>
                    <Typography sx={sectionLabelSx}>
                      {labels.angle ?? "Angle"}
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      value={normalizeAngleInputValue(state.linearAngle)}
                      disabled={disabled}
                      placeholder="90"
                      slotProps={{ htmlInput: { step: "any" } }}
                      onChange={(e) =>
                        emit({
                          linearAngle: parseAngleInputValue(e.target.value),
                        })
                      }
                      sx={inputSx}
                    />
                  </Box>
                ) : (
                  <Box>
                    <Typography sx={sectionLabelSx}>
                      {labels.side ?? "Side"}
                    </Typography>
                    <Select
                      size="small"
                      fullWidth
                      value={state.linearSide}
                      disabled={disabled}
                      onChange={(e) =>
                        emit({ linearSide: String(e.target.value) })
                      }
                      sx={selectSx}
                      MenuProps={editorPickerSelectMenuProps}
                    >
                      {withCurrentOption(
                        LINEAR_SIDE_OPTIONS,
                        state.linearSide,
                      ).map((option) => (
                        <MenuItem key={option} value={option}>
                          {getOptionLabel(
                            option,
                            LINEAR_SIDE_LABEL_KEYS,
                            labels,
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Radial controls */}
          {isRadial && (
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={sectionLabelSx}>
                {labels.radialSection ?? "Radial"}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 1,
                }}
              >
                <Box>
                  <Typography sx={sectionLabelSx}>
                    {labels.shape ?? "Shape"}
                  </Typography>
                  <Select
                    size="small"
                    fullWidth
                    value={state.radialShape}
                    disabled={disabled}
                    onChange={(e) => emit({ radialShape: e.target.value })}
                    sx={selectSx}
                    MenuProps={editorPickerSelectMenuProps}
                  >
                    <MenuItem value="ellipse">ellipse</MenuItem>
                    <MenuItem value="circle">circle</MenuItem>
                  </Select>
                </Box>
                <Box>
                  <Typography sx={sectionLabelSx}>
                    {labels.size ?? "Size"}
                  </Typography>
                  <Select
                    size="small"
                    fullWidth
                    value={state.radialSize}
                    disabled={disabled}
                    onChange={(e) =>
                      emit({ radialSize: String(e.target.value) })
                    }
                    sx={selectSx}
                    MenuProps={editorPickerSelectMenuProps}
                  >
                    {withCurrentOption(
                      RADIAL_SIZE_OPTIONS,
                      state.radialSize,
                    ).map((option) => (
                      <MenuItem key={option} value={option}>
                        {getOptionLabel(option, RADIAL_SIZE_LABEL_KEYS, labels)}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box>
                  <Typography sx={sectionLabelSx}>
                    {labels.position ?? "Position"}
                  </Typography>
                  <Select
                    size="small"
                    fullWidth
                    value={state.radialPosition}
                    disabled={disabled}
                    onChange={(e) =>
                      emit({ radialPosition: String(e.target.value) })
                    }
                    sx={selectSx}
                    MenuProps={editorPickerSelectMenuProps}
                  >
                    {withCurrentOption(
                      POSITION_OPTIONS,
                      state.radialPosition,
                    ).map((option) => (
                      <MenuItem key={option} value={option}>
                        {getOptionLabel(option, POSITION_LABEL_KEYS, labels)}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
            </Box>
          )}

          {/* Conic controls */}
          {isConic && (
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={sectionLabelSx}>
                {labels.conicSection ?? "Conic"}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1,
                }}
              >
                <Box>
                  <Typography sx={sectionLabelSx}>
                    {labels.from ?? "From"}
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    value={normalizeAngleInputValue(state.conicFrom)}
                    disabled={disabled}
                    placeholder="0"
                    slotProps={{ htmlInput: { step: "any" } }}
                    onChange={(e) =>
                      emit({ conicFrom: parseAngleInputValue(e.target.value) })
                    }
                    sx={inputSx}
                  />
                </Box>
                <Box>
                  <Typography sx={sectionLabelSx}>
                    {labels.position ?? "Position"}
                  </Typography>
                  <Select
                    size="small"
                    fullWidth
                    value={state.conicPosition}
                    disabled={disabled}
                    onChange={(e) =>
                      emit({ conicPosition: String(e.target.value) })
                    }
                    sx={selectSx}
                    MenuProps={editorPickerSelectMenuProps}
                  >
                    {withCurrentOption(
                      POSITION_OPTIONS,
                      state.conicPosition,
                    ).map((option) => (
                      <MenuItem key={option} value={option}>
                        {getOptionLabel(option, POSITION_LABEL_KEYS, labels)}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
            </Box>
          )}

          {/* Color space interpolation */}
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={sectionLabelSx}>
              {labels.interpolation ?? "Color interpolation"}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isPolarSpace ? "1fr 1fr" : "1fr",
                gap: 1,
              }}
            >
              <Box>
                <Typography sx={sectionLabelSx}>
                  {labels.colorSpace ?? "Space"}
                </Typography>
                <Select
                  size="small"
                  fullWidth
                  value={state.colorSpace}
                  disabled={disabled}
                  onChange={(e) => emit({ colorSpace: e.target.value })}
                  sx={selectSx}
                  MenuProps={editorPickerSelectMenuProps}
                >
                  <MenuItem value="">
                    {labels.defaultSpace ?? "default"}
                  </MenuItem>
                  {COLOR_SPACES.filter(Boolean).map((space) => (
                    <MenuItem key={space} value={space}>
                      {space}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              {isPolarSpace && (
                <Box>
                  <Typography sx={sectionLabelSx}>
                    {labels.hueMethod ?? "Hue"}
                  </Typography>
                  <Select
                    size="small"
                    fullWidth
                    value={state.hueMethod}
                    disabled={disabled}
                    onChange={(e) => emit({ hueMethod: e.target.value })}
                    sx={selectSx}
                    MenuProps={editorPickerSelectMenuProps}
                  >
                    {HUE_METHODS.map((m) => (
                      <MenuItem key={m || "default"} value={m}>
                        {m || (labels.defaultHue ?? "default")}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}
            </Box>
          </Box>

          {/* Color stops */}
          <Box sx={{ mb: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 0.75,
              }}
            >
              <Typography sx={{ ...sectionLabelSx, mb: 0 }}>
                {labels.stopsLabel ?? "Color stops"}
              </Typography>
              <IconButton
                size="small"
                disabled={disabled}
                onClick={addStop}
                sx={{
                  border: "1px solid #c7d2e0",
                  borderRadius: "6px",
                  width: 28,
                  height: 28,
                  color: "#334155",
                  "&:hover": { borderColor: "#94a3b8", bgcolor: "#f1f5f9" },
                }}
              >
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
            <Box sx={{ display: "grid", gap: 1 }}>
              {state.stops.map((stop, index) => (
                <Box
                  key={index}
                  sx={{
                    border: "1px solid #dbe4ef",
                    borderRadius: "8px",
                    bgcolor: "#fff",
                    p: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 700, color: "#334155" }}
                    >
                      {(labels.stopLabel ?? "Stop") + ` ${index + 1}`}
                    </Typography>
                    <IconButton
                      size="small"
                      disabled={disabled}
                      onClick={() => removeStop(index)}
                      sx={{
                        width: 26,
                        height: 26,
                        border: "1px solid #c7d2e0",
                        borderRadius: "6px",
                        color: "#334155",
                        "&:hover": {
                          borderColor: "#ef4444",
                          color: "#ef4444",
                          bgcolor: "#fff7f7",
                        },
                        "&.Mui-disabled": { opacity: 0.35 },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Box>
                      <Typography sx={sectionLabelSx}>
                        {labels.color ?? "Color"}
                      </Typography>
                      <ColorPicker
                        value={stop.color}
                        disabled={disabled}
                        onChange={(next) => updateStop(index, { color: next })}
                      />
                    </Box>
                    <Box>
                      <Typography sx={sectionLabelSx}>
                        {isConic
                          ? (labels.angleA ?? "Angle A")
                          : (labels.positionA ?? "Position A")}
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        value={
                          isConic
                            ? normalizeAngleInputValue(stop.positionA)
                            : normalizeStopInputValue(stop.positionA, false)
                        }
                        disabled={disabled}
                        placeholder={isConic ? "0" : "0"}
                        slotProps={{ htmlInput: { step: "any" } }}
                        onChange={(e) =>
                          updateStop(index, {
                            positionA: isConic
                              ? parseAngleInputValue(e.target.value)
                              : parseStopInputValue(e.target.value, false),
                          })
                        }
                        sx={inputSx}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography sx={sectionLabelSx}>
                        {isConic
                          ? (labels.angleB ?? "Angle B")
                          : (labels.positionB ?? "Position B")}
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        value={
                          isConic
                            ? normalizeAngleInputValue(stop.positionB)
                            : normalizeStopInputValue(stop.positionB, false)
                        }
                        disabled={disabled}
                        placeholder={isConic ? "90" : "50"}
                        slotProps={{ htmlInput: { step: "any" } }}
                        onChange={(e) =>
                          updateStop(index, {
                            positionB: isConic
                              ? parseAngleInputValue(e.target.value)
                              : parseStopInputValue(e.target.value, false),
                          })
                        }
                        sx={inputSx}
                      />
                    </Box>
                    <Box>
                      <Typography sx={sectionLabelSx}>
                        {labels.hint ?? "Hint"}
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        value={
                          isConic
                            ? normalizeAngleInputValue(stop.hint)
                            : normalizeStopInputValue(stop.hint, false)
                        }
                        disabled={disabled}
                        placeholder={isConic ? "45" : "30"}
                        slotProps={{ htmlInput: { step: "any" } }}
                        onChange={(e) =>
                          updateStop(index, {
                            hint: isConic
                              ? parseAngleInputValue(e.target.value)
                              : parseStopInputValue(e.target.value, false),
                          })
                        }
                        sx={inputSx}
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Preview */}
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={sectionLabelSx}>
              {labels.preview ?? "Preview"}
            </Typography>
            <Box
              sx={{
                position: "relative",
                height: 80,
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid #c7d2e0",
                background: checkerboardBg,
                backgroundSize: "14px 14px",
                backgroundPosition: "0 0, 0 7px, 7px -7px, -7px 0",
              }}
            >
              <Box
                sx={{ position: "absolute", inset: 0, background: gradientCss }}
              />
            </Box>
          </Box>

          {/* Raw mode */}
          <Box
            sx={{
              borderTop: "1px solid #e2e8f0",
              pt: 1.5,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={state.rawMode}
                  disabled={disabled}
                  onChange={(e) => {
                    const nextRaw = e.target.checked;
                    if (nextRaw && !state.raw.trim()) {
                      emit({ rawMode: true, raw: buildGradient(state) });
                    } else {
                      emit({ rawMode: nextRaw });
                    }
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: 12, color: "#334155" }}>
                  {labels.rawMode ?? "Raw CSS"}
                </Typography>
              }
              sx={{ mb: 1, ml: -0.5 }}
            />
            {state.rawMode && (
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={state.raw}
                disabled={disabled}
                placeholder="linear-gradient(in oklab, red, blue)"
                onChange={(e) => emit({ raw: e.target.value })}
                sx={{
                  "& .MuiInputBase-root": {
                    borderRadius: "6px",
                    fontFamily: "Menlo, Consolas, monospace",
                    fontSize: 12,
                    lineHeight: 1.45,
                    color: "#334155",
                  },
                }}
              />
            )}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

const sectionLabelSx = {
  display: "block",
  fontSize: 12,
  color: "#64748b",
  mb: 0.75,
};

const inputSx = {
  "& .MuiInputBase-root": {
    height: 40,
    borderRadius: "6px",
    "& input": { fontSize: 13 },
  },
};

const selectSx = {
  height: 40,
  borderRadius: "6px",
  fontSize: 13,
};

export default GradientPicker;
